import useSWR, { mutate } from 'swr';
import { CACHE_KEYS } from '@/lib/swr-config';
import type { PageWithComponents } from '@/services/page/types';

const API_BASE = '/api/directus';

async function directusFetcher<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Directus fetch failed: ${res.status}`);
  return res.json();
}

export function usePageData(slug: string, fallbackData?: PageWithComponents) {
  const { data, error, isLoading } = useSWR<PageWithComponents>(
    slug ? `${API_BASE}/pages?slug=${encodeURIComponent(slug)}` : null,
    directusFetcher,
    {
      fallbackData,
      refreshInterval: 3600000,
      dedupingInterval: 60000,
    }
  );
  return { page: data, isLoading, isError: !!error };
}

export function useBlogPosts(page: number, perPage = 10, fallbackData?: unknown) {
  const { data, error, isLoading } = useSWR(
    `${API_BASE}/blog_posts?page=${page}&per_page=${perPage}`,
    directusFetcher,
    {
      fallbackData,
      refreshInterval: 1800000,
      dedupingInterval: 30000,
    }
  );
  return { posts: data, isLoading, isError: !!error };
}

export function useBlogPost(slug: string, fallbackData?: unknown) {
  const { data, error, isLoading } = useSWR(
    slug ? `${API_BASE}/blog_posts?slug=${encodeURIComponent(slug)}` : null,
    directusFetcher,
    {
      fallbackData,
      refreshInterval: 1800000,
      dedupingInterval: 60000,
    }
  );
  return { post: data, isLoading, isError: !!error };
}

export function useGalleryCategories(fallbackData?: unknown) {
  const { data, error, isLoading } = useSWR(
    `${API_BASE}/gallery_categories`,
    directusFetcher,
    {
      fallbackData,
      refreshInterval: 3600000,
      dedupingInterval: 60000,
    }
  );
  return { categories: data, isLoading, isError: !!error };
}

export const prefetch = {
  pageData: (slug: string) =>
    mutate(
      `${API_BASE}/pages?slug=${encodeURIComponent(slug)}`,
      directusFetcher(`${API_BASE}/pages?slug=${encodeURIComponent(slug)}`),
      { revalidate: false }
    ),

  blogPosts: (page: number, perPage = 6) =>
    mutate(
      `${API_BASE}/blog_posts?page=${page}&per_page=${perPage}`,
      directusFetcher(`${API_BASE}/blog_posts?page=${page}&per_page=${perPage}`),
      { revalidate: false }
    ),
};

export function invalidateDirectusCache(pattern?: string) {
  if (pattern) {
    mutate(
      (key: string) => typeof key === 'string' && key.includes(pattern),
      undefined,
      { revalidate: true }
    );
  } else {
    mutate(
      (key: string) => typeof key === 'string' && key.startsWith(API_BASE),
      undefined,
      { revalidate: true }
    );
  }
}
